import { FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { config } from '../lib/config';
import { CHALLENGE } from '../lib/constants';
import {
  loadRedirectContext,
  navigatePostLogin,
  resolvePostLoginDestination,
} from '../lib/redirect';
import { BrandLogo } from '../components/BrandLogo';
import { PasswordField } from '../components/PasswordField';

export function SignInPage(): JSX.Element {
  const { challenge, login, completeNewPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;

  useEffect(() => {
    loadRedirectContext();
  }, []);

  async function handlePostLogin() {
    const destination = await resolvePostLoginDestination();
    navigatePostLogin(destination, navigate);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (challenge?.challenge === CHALLENGE.NEW_PASSWORD_REQUIRED) {
        await completeNewPassword(email, newPassword);
      } else {
        const result = await login(email, password);
        if (result.challenge) {
          return;
        }
      }
      await handlePostLogin();
    } catch (submissionError) {
      setError(
        submissionError instanceof Error ? submissionError.message : 'Authentication failed',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <BrandLogo />
        <h1>Sign in</h1>
        <p className="login-card__copy">
          Sign in to access Sense AI services. After authentication you will return to your app or
          choose where to continue.
        </p>

        <form className="login-form" onSubmit={handleSubmit}>
          <label>
            <span>Email</span>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              required
              autoComplete="username"
            />
          </label>

          {challenge?.challenge === CHALLENGE.NEW_PASSWORD_REQUIRED ? (
            <PasswordField
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              minLength={config.passwordMinLength}
              autoComplete="new-password"
            />
          ) : (
            <PasswordField
              label="Password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
          )}

          {notice ? <p className="hint-text">{notice}</p> : null}
          {error ? <div className="form-error">{error}</div> : null}

          <button className="primary-button" disabled={loading} type="submit">
            {loading
              ? 'Authenticating...'
              : challenge?.challenge === CHALLENGE.NEW_PASSWORD_REQUIRED
                ? 'Set new password'
                : 'Sign in'}
          </button>
        </form>

        <p className="hint-text">
          <Link className="text-link" to={`/forgot-password${location.search}`}>
            Forgot password?
          </Link>
        </p>

        <p className="hint-text">
          New to Sense AI?{' '}
          <Link className="text-link" to={`/signup${location.search}`}>
            Create an account
          </Link>
        </p>

        <p className="hint-text">
          <Link className="text-link" to="/partner/login">
            Partner sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
